import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import {
  RevenueOverviewReport,
  RevenueByDimensionReport,
  ChargeRateReport,
  BookingVolumeReport,
  FillRateReport,
  CancellationReport,
  WorkerDeploymentReport,
  TimesheetApprovalReport,
  PipelineSnapshotReport,
  ConversionFunnelReport,
  PlacementReportData,
  WorkerRegistrationReport,
  WorkerUtilisationReport,
  WorkerComplianceReport,
  CompanyOverviewReport,
  CompanyDormancyReport,
  CompanyProfitabilityReport,
  LocationOverviewReport,
  LocationDormancyReport,
  LocationProfitabilityReport,
  ActivityLeaderboardReport,
  ActivityMixReport,
  CallEffectivenessReport,
  TaskManagementReport,
  CampaignPerformanceReport,
  OpportunityOverviewReport,
  OpportunityDormancyReport,
  PayrollSummaryReport,
  ExecutiveSummaryReport,
  PeriodComparisonReport,
  LeagueTableReport,
  CvsParsedByUserReport,
  SpecOutVolumeReport,
} from '@/models/Reports/ManagementReportModels';

export interface IManagementReportsState {
  loading: boolean;
  revenueOverview: RevenueOverviewReport | null;
  revenueByCompany: RevenueByDimensionReport | null;
  revenueByPosition: RevenueByDimensionReport | null;
  revenueTrend: RevenueByDimensionReport | null;
  revenueByConsultant: RevenueByDimensionReport | null;
  chargeRateAnalysis: ChargeRateReport | null;
  bookingVolume: BookingVolumeReport | null;
  fillRate: FillRateReport | null;
  cancellations: CancellationReport | null;
  workerDeployment: WorkerDeploymentReport | null;
  timesheetApproval: TimesheetApprovalReport | null;
  pipelineSnapshot: PipelineSnapshotReport | null;
  conversionFunnel: ConversionFunnelReport | null;
  placements: PlacementReportData | null;
  workerRegistration: WorkerRegistrationReport | null;
  workerUtilisation: WorkerUtilisationReport | null;
  workerCompliance: WorkerComplianceReport | null;
  companyOverview: CompanyOverviewReport | null;
  companyDormancy: CompanyDormancyReport | null;
  companyProfitability: CompanyProfitabilityReport | null;
  locationOverview: LocationOverviewReport | null;
  locationDormancy: LocationDormancyReport | null;
  locationProfitability: LocationProfitabilityReport | null;
  activityLeaderboard: ActivityLeaderboardReport | null;
  activityMix: ActivityMixReport | null;
  callEffectiveness: CallEffectivenessReport | null;
  taskManagement: TaskManagementReport | null;
  campaignPerformance: CampaignPerformanceReport | null;
  opportunityOverview: OpportunityOverviewReport | null;
  opportunityDormancy: OpportunityDormancyReport | null;
  payrollSummary: PayrollSummaryReport | null;
  executiveSummary: ExecutiveSummaryReport | null;
  periodComparison: PeriodComparisonReport | null;
  leagueTable: LeagueTableReport | null;
  cvsParsedByUser: CvsParsedByUserReport | null;
  specOutVolume: SpecOutVolumeReport | null;
}

function buildQueryString(params: ReportFilterParams): string {
  const qs = new URLSearchParams();
  const record = params as unknown as Record<string, string | undefined>;
  Object.entries(record).forEach(([key, val]) => {
    if (val) qs.append(key, val);
  });
  const s = qs.toString();
  return s ? '?' + s : '';
}

export interface ReportFilterParams {
  periodFrom: string;
  periodTo: string;
  groupBy?: string;
  branchId?: string;
  deskId?: string;
  userId?: string;
  companyId?: string;
  locationId?: string;
  positionId?: string;
  comparePeriodFrom?: string;
  comparePeriodTo?: string;
}

// Standalone fetch helper — called by actions, returns data or null
async function fetchReportData(endpoint: string, params: ReportFilterParams): Promise<any> {
  const qs = buildQueryString(params);
  try {
    const response = await ajax.get(`/api/v1/agency/management-reports/${endpoint}${qs}`);
    return response.data;
  } catch (error) {
    AppModule.handleApiError(error);
    return null;
  }
}

@Module({ dynamic: true, store, name: 'ManagementReports', namespaced: true })
class ManagementReports extends VuexModule implements IManagementReportsState {

  public loading = false;
  public revenueOverview: RevenueOverviewReport | null = null;
  public revenueByCompany: RevenueByDimensionReport | null = null;
  public revenueByPosition: RevenueByDimensionReport | null = null;
  public revenueTrend: RevenueByDimensionReport | null = null;
  public revenueByConsultant: RevenueByDimensionReport | null = null;
  public chargeRateAnalysis: ChargeRateReport | null = null;
  public bookingVolume: BookingVolumeReport | null = null;
  public fillRate: FillRateReport | null = null;
  public cancellations: CancellationReport | null = null;
  public workerDeployment: WorkerDeploymentReport | null = null;
  public timesheetApproval: TimesheetApprovalReport | null = null;
  public pipelineSnapshot: PipelineSnapshotReport | null = null;
  public conversionFunnel: ConversionFunnelReport | null = null;
  public placements: PlacementReportData | null = null;
  public workerRegistration: WorkerRegistrationReport | null = null;
  public workerUtilisation: WorkerUtilisationReport | null = null;
  public workerCompliance: WorkerComplianceReport | null = null;
  public companyOverview: CompanyOverviewReport | null = null;
  public companyDormancy: CompanyDormancyReport | null = null;
  public companyProfitability: CompanyProfitabilityReport | null = null;
  public locationOverview: LocationOverviewReport | null = null;
  public locationDormancy: LocationDormancyReport | null = null;
  public locationProfitability: LocationProfitabilityReport | null = null;
  public activityLeaderboard: ActivityLeaderboardReport | null = null;
  public activityMix: ActivityMixReport | null = null;
  public callEffectiveness: CallEffectivenessReport | null = null;
  public taskManagement: TaskManagementReport | null = null;
  public campaignPerformance: CampaignPerformanceReport | null = null;
  public opportunityOverview: OpportunityOverviewReport | null = null;
  public opportunityDormancy: OpportunityDormancyReport | null = null;
  public payrollSummary: PayrollSummaryReport | null = null;
  public executiveSummary: ExecutiveSummaryReport | null = null;
  public periodComparison: PeriodComparisonReport | null = null;
  public leagueTable: LeagueTableReport | null = null;
  public cvsParsedByUser: CvsParsedByUserReport | null = null;
  public specOutVolume: SpecOutVolumeReport | null = null;

  // 4.1 Revenue & Profitability
  @Action
  public async loadRevenueOverview(params: ReportFilterParams) {
    this.context.commit('setLoading', true);
    const data = await fetchReportData('revenue/overview', params);
    if (data) this.context.commit('setRevenueOverview', data);
    this.context.commit('setLoading', false);
  }

  @Action
  public async loadRevenueByCompany(params: ReportFilterParams) {
    const data = await fetchReportData('revenue/by-company', params);
    if (data) this.context.commit('setRevenueByCompany', data);
  }

  @Action
  public async loadRevenueByPosition(params: ReportFilterParams) {
    const data = await fetchReportData('revenue/by-position', params);
    if (data) this.context.commit('setRevenueByPosition', data);
  }

  @Action
  public async loadRevenueTrend(params: ReportFilterParams) {
    const data = await fetchReportData('revenue/trend', params);
    if (data) this.context.commit('setRevenueTrend', data);
  }

  @Action
  public async loadRevenueByConsultant(params: ReportFilterParams) {
    const data = await fetchReportData('revenue/by-consultant', params);
    if (data) this.context.commit('setRevenueByConsultant', data);
  }

  @Action
  public async loadChargeRateAnalysis(params: ReportFilterParams) {
    const data = await fetchReportData('revenue/charge-rates', params);
    if (data) this.context.commit('setChargeRateAnalysis', data);
  }

  // 4.2 Temp Operations
  @Action
  public async loadBookingVolume(params: ReportFilterParams) {
    const data = await fetchReportData('temp-ops/booking-volume', params);
    if (data) this.context.commit('setBookingVolume', data);
  }

  @Action
  public async loadFillRate(params: ReportFilterParams) {
    const data = await fetchReportData('temp-ops/fill-rate', params);
    if (data) this.context.commit('setFillRate', data);
  }

  @Action
  public async loadCancellations(params: ReportFilterParams) {
    const data = await fetchReportData('temp-ops/cancellations', params);
    if (data) this.context.commit('setCancellations', data);
  }

  @Action
  public async loadWorkerDeployment(params: ReportFilterParams) {
    const data = await fetchReportData('temp-ops/worker-deployment', params);
    if (data) this.context.commit('setWorkerDeployment', data);
  }

  @Action
  public async loadTimesheetApproval(params: ReportFilterParams) {
    const data = await fetchReportData('temp-ops/timesheet-approval', params);
    if (data) this.context.commit('setTimesheetApproval', data);
  }

  // 4.3 Perm Recruitment
  @Action
  public async loadPipelineSnapshot(params: ReportFilterParams) {
    const data = await fetchReportData('perm/pipeline', params);
    if (data) this.context.commit('setPipelineSnapshot', data);
  }

  @Action
  public async loadConversionFunnel(params: ReportFilterParams) {
    const data = await fetchReportData('perm/conversion-funnel', params);
    if (data) this.context.commit('setConversionFunnel', data);
  }

  @Action
  public async loadPlacements(params: ReportFilterParams) {
    const data = await fetchReportData('perm/placements', params);
    if (data) this.context.commit('setPlacements', data);
  }

  // 4.4 Workers & Workforce
  @Action
  public async loadWorkerRegistration(params: ReportFilterParams) {
    const data = await fetchReportData('workers/registration', params);
    if (data) this.context.commit('setWorkerRegistration', data);
  }

  @Action
  public async loadWorkerUtilisation(params: ReportFilterParams) {
    const data = await fetchReportData('workers/utilisation', params);
    if (data) this.context.commit('setWorkerUtilisation', data);
  }

  @Action
  public async loadWorkerCompliance(params: ReportFilterParams) {
    const data = await fetchReportData('workers/compliance', params);
    if (data) this.context.commit('setWorkerCompliance', data);
  }

  // 4.5 Company Management
  @Action
  public async loadCompanyOverview(params: ReportFilterParams) {
    const data = await fetchReportData('companies/overview', params);
    if (data) this.context.commit('setCompanyOverview', data);
  }

  @Action
  public async loadCompanyDormancy(params: ReportFilterParams) {
    const data = await fetchReportData('companies/dormancy', params);
    if (data) this.context.commit('setCompanyDormancy', data);
  }

  @Action
  public async loadCompanyProfitability(params: ReportFilterParams) {
    const data = await fetchReportData('companies/profitability', params);
    if (data) this.context.commit('setCompanyProfitability', data);
  }

  // 4.5b Location Management
  @Action
  public async loadLocationOverview(params: ReportFilterParams) {
    const data = await fetchReportData('locations/overview', params);
    if (data) this.context.commit('setLocationOverview', data);
  }

  @Action
  public async loadLocationDormancy(params: ReportFilterParams) {
    const data = await fetchReportData('locations/dormancy', params);
    if (data) this.context.commit('setLocationDormancy', data);
  }

  @Action
  public async loadLocationProfitability(params: ReportFilterParams) {
    const data = await fetchReportData('locations/profitability', params);
    if (data) this.context.commit('setLocationProfitability', data);
  }

  // 4.6 Activity & Productivity
  @Action
  public async loadActivityLeaderboard(params: ReportFilterParams) {
    const data = await fetchReportData('activity/leaderboard', params);
    if (data) this.context.commit('setActivityLeaderboard', data);
  }

  @Action
  public async loadActivityMix(params: ReportFilterParams) {
    const data = await fetchReportData('activity/mix', params);
    if (data) this.context.commit('setActivityMix', data);
  }

  @Action
  public async loadCallEffectiveness(params: ReportFilterParams) {
    const data = await fetchReportData('activity/call-effectiveness', params);
    if (data) this.context.commit('setCallEffectiveness', data);
  }

  @Action
  public async loadTaskManagement(params: ReportFilterParams) {
    const data = await fetchReportData('activity/tasks', params);
    if (data) this.context.commit('setTaskManagement', data);
  }

  // 4.4b CVs Parsed
  @Action
  public async loadCvsParsedByUser(params: ReportFilterParams) {
    const data = await fetchReportData('workers/cvs-parsed', params);
    if (data) this.context.commit('setCvsParsedByUser', data);
  }

  // 4.9 Spec Outs
  @Action
  public async loadSpecOutVolume(params: ReportFilterParams) {
    const data = await fetchReportData('spec-outs/volume', params);
    if (data) this.context.commit('setSpecOutVolume', data);
  }

  // 4.7 Campaign & Marketing
  @Action
  public async loadCampaignPerformance(params: ReportFilterParams) {
    const data = await fetchReportData('campaigns/performance', params);
    if (data) this.context.commit('setCampaignPerformance', data);
  }

  // 4.6b Opportunity Management
  @Action
  public async loadOpportunityOverview(params: ReportFilterParams) {
    const data = await fetchReportData('opportunities/overview', params);
    if (data) this.context.commit('setOpportunityOverview', data);
  }

  @Action
  public async loadOpportunityDormancy(params: ReportFilterParams) {
    const data = await fetchReportData('opportunities/dormancy', params);
    if (data) this.context.commit('setOpportunityDormancy', data);
  }

  // 4.8 Financial
  @Action
  public async loadPayrollSummary(params: ReportFilterParams) {
    const data = await fetchReportData('financial/payroll', params);
    if (data) this.context.commit('setPayrollSummary', data);
  }

  // 4.10 Comparative & Executive
  @Action
  public async loadExecutiveSummary(params: ReportFilterParams) {
    const data = await fetchReportData('executive/summary', params);
    if (data) this.context.commit('setExecutiveSummary', data);
  }

  @Action
  public async loadPeriodComparison(params: ReportFilterParams) {
    const data = await fetchReportData('executive/period-comparison', params);
    if (data) this.context.commit('setPeriodComparison', data);
  }

  @Action
  public async loadLeagueTable(params: ReportFilterParams) {
    const data = await fetchReportData('executive/league-table', params);
    if (data) this.context.commit('setLeagueTable', data);
  }

  @Action
  cleanState() {
    this.context.commit('resetState');
  }

  // Mutations
  @Mutation resetState() {
    this.loading = false;
    this.revenueOverview = null;
    this.revenueByCompany = null;
    this.revenueByPosition = null;
    this.revenueTrend = null;
    this.revenueByConsultant = null;
    this.chargeRateAnalysis = null;
    this.bookingVolume = null;
    this.fillRate = null;
    this.cancellations = null;
    this.workerDeployment = null;
    this.timesheetApproval = null;
    this.pipelineSnapshot = null;
    this.conversionFunnel = null;
    this.placements = null;
    this.workerRegistration = null;
    this.workerUtilisation = null;
    this.workerCompliance = null;
    this.companyOverview = null;
    this.companyDormancy = null;
    this.companyProfitability = null;
    this.locationOverview = null;
    this.locationDormancy = null;
    this.locationProfitability = null;
    this.activityLeaderboard = null;
    this.activityMix = null;
    this.callEffectiveness = null;
    this.taskManagement = null;
    this.campaignPerformance = null;
    this.opportunityOverview = null;
    this.opportunityDormancy = null;
    this.payrollSummary = null;
    this.executiveSummary = null;
    this.periodComparison = null;
    this.leagueTable = null;
    this.cvsParsedByUser = null;
    this.specOutVolume = null;
  }

  @Mutation private setLoading(v: boolean) { this.loading = v; }
  @Mutation private setRevenueOverview(v: RevenueOverviewReport) { this.revenueOverview = v; }
  @Mutation private setRevenueByCompany(v: RevenueByDimensionReport) { this.revenueByCompany = v; }
  @Mutation private setRevenueByPosition(v: RevenueByDimensionReport) { this.revenueByPosition = v; }
  @Mutation private setRevenueTrend(v: RevenueByDimensionReport) { this.revenueTrend = v; }
  @Mutation private setRevenueByConsultant(v: RevenueByDimensionReport) { this.revenueByConsultant = v; }
  @Mutation private setChargeRateAnalysis(v: ChargeRateReport) { this.chargeRateAnalysis = v; }
  @Mutation private setBookingVolume(v: BookingVolumeReport) { this.bookingVolume = v; }
  @Mutation private setFillRate(v: FillRateReport) { this.fillRate = v; }
  @Mutation private setCancellations(v: CancellationReport) { this.cancellations = v; }
  @Mutation private setWorkerDeployment(v: WorkerDeploymentReport) { this.workerDeployment = v; }
  @Mutation private setTimesheetApproval(v: TimesheetApprovalReport) { this.timesheetApproval = v; }
  @Mutation private setPipelineSnapshot(v: PipelineSnapshotReport) { this.pipelineSnapshot = v; }
  @Mutation private setConversionFunnel(v: ConversionFunnelReport) { this.conversionFunnel = v; }
  @Mutation private setPlacements(v: PlacementReportData) { this.placements = v; }
  @Mutation private setWorkerRegistration(v: WorkerRegistrationReport) { this.workerRegistration = v; }
  @Mutation private setWorkerUtilisation(v: WorkerUtilisationReport) { this.workerUtilisation = v; }
  @Mutation private setWorkerCompliance(v: WorkerComplianceReport) { this.workerCompliance = v; }
  @Mutation private setCompanyOverview(v: CompanyOverviewReport) { this.companyOverview = v; }
  @Mutation private setCompanyDormancy(v: CompanyDormancyReport) { this.companyDormancy = v; }
  @Mutation private setCompanyProfitability(v: CompanyProfitabilityReport) { this.companyProfitability = v; }
  @Mutation private setLocationOverview(v: LocationOverviewReport) { this.locationOverview = v; }
  @Mutation private setLocationDormancy(v: LocationDormancyReport) { this.locationDormancy = v; }
  @Mutation private setLocationProfitability(v: LocationProfitabilityReport) { this.locationProfitability = v; }
  @Mutation private setActivityLeaderboard(v: ActivityLeaderboardReport) { this.activityLeaderboard = v; }
  @Mutation private setActivityMix(v: ActivityMixReport) { this.activityMix = v; }
  @Mutation private setCallEffectiveness(v: CallEffectivenessReport) { this.callEffectiveness = v; }
  @Mutation private setTaskManagement(v: TaskManagementReport) { this.taskManagement = v; }
  @Mutation private setCampaignPerformance(v: CampaignPerformanceReport) { this.campaignPerformance = v; }
  @Mutation private setOpportunityOverview(v: OpportunityOverviewReport) { this.opportunityOverview = v; }
  @Mutation private setOpportunityDormancy(v: OpportunityDormancyReport) { this.opportunityDormancy = v; }
  @Mutation private setPayrollSummary(v: PayrollSummaryReport) { this.payrollSummary = v; }
  @Mutation private setExecutiveSummary(v: ExecutiveSummaryReport) { this.executiveSummary = v; }
  @Mutation private setPeriodComparison(v: PeriodComparisonReport) { this.periodComparison = v; }
  @Mutation private setLeagueTable(v: LeagueTableReport) { this.leagueTable = v; }
  @Mutation private setCvsParsedByUser(v: CvsParsedByUserReport) { this.cvsParsedByUser = v; }
  @Mutation private setSpecOutVolume(v: SpecOutVolumeReport) { this.specOutVolume = v; }
}

export const ManagementReportsModule = getModule(ManagementReports);
